import React, { useCallback } from 'react';
import {
  AppBar,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  Theme,
  Drawer,
  Container,
} from '@material-ui/core';
import { IoIosCloseCircleOutline } from 'react-icons/io';

import { uuid } from 'uuidv4';
import { useGrupoPizza } from '../../../../../../hooks/grupoPizza';
import { useToast } from '../../../../../../hooks/toast';

import api from '../../../../../../services/api';

import { CloseButton, DrawerTitle, DrawerContainer } from '../../drawer_styles';
import TabPanel from '../../../../../Configuracoes/TabBar/TabPanel';

import Detalhes from '../GrupoPizza/tabs/Detalhes';
import Tamanhos from '../GrupoPizza/tabs/Tamanhos';
import Complementos from '../GrupoPizza/tabs/Complementos';

import { MenuOptionButton } from '../../../styles';

interface a11yPropsReturnType {
  id: string;
  'aria-controls': string;
}

interface EditGrupoPizzaProps {
  grupo_pizza_id: number;
  descricao: string;
  image?: string;
  pausar_venda: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    width: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  noShadow: {
    boxShadow: 'none',
  },
  tabBorder: {
    borderBottom: '1px solid #e0e0e0',
  },
  noPaddingTop: {
    paddingTop: '0 !important',
  },
}));

function a11yProps(index: number): a11yPropsReturnType {
  return {
    id: `scrollable-auto-tab-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-${index}`,
  };
}

const EditGrupoPizza: React.FC<EditGrupoPizzaProps> = ({
  grupo_pizza_id,
  descricao,
  image,
  pausar_venda,
}) => {
  const {
    complementosIsEnabled,
    tamanhosIsEnabled,
    currentTabIndex,
    grupoPizzaId,
    toggleEditingGrupo,
    resetDrawer,
    changeCurrentTabIndex,
    changeGrupoPizzaData,
    enableComplementos,
    enableTamanhos,
    enableItensComplementos,
  } = useGrupoPizza();
  const { addToast } = useToast();

  const classes = useStyles();

  const handleFetchGroupDetails = useCallback(async () => {
    addToast({
      title: 'Aguarde',
      description: 'Estamos obtendo as informações do grupo.',
      type: 'info',
    });

    try {
      const complementosResponse = await api.get(
        `/complemento/grupo/${grupo_pizza_id}`,
      );

      const { complementos, itens_complemento } = complementosResponse.data;

      const tamanhosResponse = await api.get(`/tamanho`);

      const tamanhos = tamanhosResponse.data;

      changeGrupoPizzaData({
        descricao,
        image,
        pausar_venda,
        tamanhos: tamanhos.map((tamanho: any) => ({
          id: tamanho.id,
          descricao: tamanho.descricao,
          numberOfSlices: tamanho.qtd_pedacos,
          numberOfFlavors: tamanho.qtd_sabores,
          isNew: false,
          ordem: tamanho.ordem,
        })),
        complementos: complementos.map((complemento: any) => ({
          ...complemento,
          qtd_min: Number(complemento.qtd_min),
          qtd_max: Number(complemento.qtd_max),
          isNew: false,
        })),
        itens_complemento: itens_complemento.map((item: any) => ({
          ...item,
          preco: Number(item.preco),
          isNew: false,
        })),
      });
      toggleEditingGrupo(grupo_pizza_id);
      enableComplementos();
      enableTamanhos();
      enableItensComplementos();
    } catch (err) {
      addToast({
        title: 'Algo deu errado',
        description: 'Ocorreu um erro ao buscar as informações do grupo.',
        type: 'info',
      });
    }
  }, [
    descricao,
    grupo_pizza_id,
    image,
    pausar_venda,
    addToast,
    enableTamanhos,
    toggleEditingGrupo,
    enableComplementos,
    changeGrupoPizzaData,
    enableItensComplementos,
  ]);

  return (
    <>
      <MenuOptionButton type="button" onClick={handleFetchGroupDetails}>
        Editar Grupo
      </MenuOptionButton>

      <Drawer
        anchor="right"
        open={grupo_pizza_id === grupoPizzaId}
        onClose={resetDrawer}
      >
        <CloseButton onClick={resetDrawer} type="button">
          <IoIosCloseCircleOutline size={24} />
        </CloseButton>

        <DrawerContainer role="presentation">
          <Container maxWidth="lg">
            <Grid item xs={12}>
              <DrawerTitle>
                <h1>Grupo Pizza</h1>
              </DrawerTitle>
            </Grid>

            <Grid>
              <AppBar
                position="static"
                color="transparent"
                className={classes.noShadow}
              >
                <Tabs
                  value={currentTabIndex}
                  onChange={(_event, tabIndex) =>
                    changeCurrentTabIndex(tabIndex)
                  }
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="grupo_pizza_tabs"
                  className={classes.tabBorder}
                >
                  <Tab label="Detalhes" {...a11yProps(0)} />
                  <Tab
                    label="Tamanhos"
                    disabled={!tamanhosIsEnabled}
                    {...a11yProps(1)}
                  />
                  <Tab
                    label="Complementos"
                    disabled={!complementosIsEnabled}
                    {...a11yProps(2)}
                  />
                </Tabs>
              </AppBar>
              <TabPanel value={currentTabIndex} index={0}>
                <Detalhes />
              </TabPanel>
              <TabPanel value={currentTabIndex} index={1}>
                <Tamanhos />
              </TabPanel>
              <TabPanel value={currentTabIndex} index={2}>
                <Complementos />
              </TabPanel>
            </Grid>
          </Container>
        </DrawerContainer>
      </Drawer>
    </>
  );
};

export default EditGrupoPizza;
